import { X } from 'lucide-react';
import { itemStats, minToTime } from './calendarLayout';
import { calType, typeVars, type CalItem, type CalSession } from './calendarTypes';

/* The detail panel behind any selected card, run or row.
 *
 * The full timeline docks it to the right of the grid; the Today tab swaps it
 * in for its side card. Same markup either way, so what a session says about
 * itself doesn't depend on which screen you opened it from. */

export function SessionDrawer({ it, dateLabel, onClose, onViewBookings, onEdit }: {
  it: CalItem;
  /** "Fri 25 Sept", already in the gym's locale — the caller knows the week. */
  dateLabel: string;
  onClose: () => void;
  onViewBookings?: (s: CalSession) => void;
  onEdit?: (s: CalSession) => void;
}) {
  if (it.kind === 'more') return null;
  const T = calType(it.type);
  const s = it.s;
  const st = itemStats(it);
  const isRun = it.kind === 'run';

  return (
    <aside className="a-drawer" style={typeVars(T)} aria-label="Session details">
      <div className="a-dr-top">
        <span className="a-dr-type"><i />{T.label}</span>
        <button className="a-ic" onClick={onClose} aria-label="Close details"><X className="w-4 h-4" /></button>
      </div>

      <h3>{isRun ? `${s.name} · ${s.res}` : s.name}</h3>
      <p className="a-dr-when">
        {dateLabel} · {minToTime(it.start)}–{minToTime(it.end)}
        {s.past ? ' · Past' : s.live ? ' · Live now' : ''}
      </p>

      <dl>
        {s.type !== 'pt' && (<><dt>Resource</dt><dd>{s.res}</dd></>)}
        {s.who && (<><dt>{s.type === 'pt' ? 'Trainer' : 'Coach'}</dt><dd>{s.who}</dd></>)}
        {s.member && (<><dt>Member</dt><dd>{s.member}</dd></>)}
        {s.branch && (<><dt>Branch</dt><dd>{s.branch}</dd></>)}
      </dl>

      <div className="a-dr-cap">
        <div className="a-dr-cap-h"><span>Booked</span><b>{st.booked} / {st.cap}</b></div>
        <div className="a-fill"><span style={{ width: `${st.fill * 100}%` }} /></div>
        {/* A run of 1:1 appointments is never "full" — it is simply booked out,
            and that is not a problem anyone needs flagged in red. */}
        {st.cap > st.n
          ? (st.wait > 0 || st.full > 0) && (
              <p>
                {isRun ? `${st.full} of ${st.n} slots full` : 'Full'}
                {st.wait ? ` · ${st.wait} on waitlist` : ''}
              </p>
            )
          : isRun && <p className="neutral">{st.full} of {st.n} appointments booked</p>}
      </div>

      {isRun && (
        <div className="a-dr-slots">
          {it.slots.map(x => (
            <div key={x.id} className={x.past ? 'past' : ''}>
              <span>{minToTime(x.start)}</span>
              <div className="a-fill"><span style={{ width: `${x.cap ? (x.booked / x.cap) * 100 : 0}%` }} /></div>
              <span>{x.booked}/{x.cap}</span>
            </div>
          ))}
        </div>
      )}

      <div className="a-dr-act">
        <button className="a-btn dark" onClick={() => onViewBookings?.(s)}>View bookings</button>
        <button className="a-btn" onClick={() => onEdit?.(s)}>Edit</button>
      </div>
    </aside>
  );
}
